import { createApi } from '@reduxjs/toolkit/query/react';

import { opsQuery } from 'store/query';

import { researchWebApi, RESEARCH_WEB_TAG } from '../research-web';

import {
  FollowUp,
  MailBadge,
  ShowEmail,
  EmailState,
  InboxThread,
  MailPreview,
  SendRequest,
  FollowUpDays,
  MailAccounts,
  MailTemplate,
  NeedsShowRow,
  InboxThreadsPage,
  MailPreviewRequest,
  MailTemplateFields,
  InboxThreadsRequest,
} from './types';

export * from './types';

// RESEARCH outreach email (TSM plan §6.6): the mailbox, templates and the
// send, on Ops under mailer/research. Its own slice; a send refreshes the
// show's mail.
const BASE = 'mailer/research';
export const RESEARCH_MAIL_TAG = 'ResearchMail' as const;
// The Inbox (S2): live Gmail pages, refreshed after a send, read/unread,
// a reminder change or a show linked.
export const RESEARCH_INBOX_TAG = 'ResearchInbox' as const;
// The sidebar badge on its own: read / unread refresh only it (review
// round 1); the thread and the list are updated in place.
export const RESEARCH_BADGE_TAG = 'ResearchMailBadge' as const;

/** The query string of an Inbox page (only what is set). */
export const threadsQuery = ({ q, filter, pageToken }: InboxThreadsRequest) => {
  const params = new URLSearchParams();
  if (filter && filter !== 'all') {
    params.set('filter', filter);
  }
  if (q?.trim()) {
    params.set('q', q.trim());
  }
  if (pageToken) {
    params.set('pageToken', pageToken);
  }
  const text = params.toString();
  return text ? `?${text}` : '';
};

const headers = { 'allow-not-found': 'true' };

// A mail action changes the Research status line too (unsure sends,
// replies needing a show, new replies): it is refreshed after each.
const refreshStatusLine = {
  onQueryStarted: async (
    _: unknown,
    {
      dispatch,
      queryFulfilled,
    }: {
      queryFulfilled: Promise<unknown>;
      dispatch: (action: unknown) => unknown;
    }
  ) => {
    try {
      await queryFulfilled;
    } finally {
      dispatch(researchWebApi.util.invalidateTags([RESEARCH_WEB_TAG]));
    }
  },
};

export const researchMailApi = createApi({
  baseQuery: opsQuery,
  reducerPath: 'researchMailApi',
  tagTypes: [RESEARCH_MAIL_TAG, RESEARCH_INBOX_TAG, RESEARCH_BADGE_TAG],
  endpoints: (build) => ({
    emailState: build.query<EmailState, number>({
      query: (id) => ({ headers, method: 'get', url: `${BASE}/emails/${id}` }),
    }),
    mailBadge: build.query<MailBadge, void>({
      providesTags: [RESEARCH_BADGE_TAG],
      query: () => ({ headers, method: 'get', url: `${BASE}/mail/badge` }),
    }),
    mailAccounts: build.query<MailAccounts, void>({
      providesTags: [RESEARCH_MAIL_TAG],
      query: () => ({ headers, method: 'get', url: `${BASE}/mail/accounts` }),
    }),
    previewMail: build.mutation<MailPreview, MailPreviewRequest>({
      query: (data) => ({
        data,
        method: 'post',
        url: `${BASE}/emails/preview`,
      }),
    }),
    connectMail: build.mutation<{ url: string }, void>({
      query: () => ({
        method: 'post',
        data: { shared: true },
        url: `${BASE}/mail/accounts/connect`,
      }),
    }),
    disconnectMail: build.mutation<{ id: number }, number>({
      invalidatesTags: [RESEARCH_MAIL_TAG],
      ...refreshStatusLine,
      query: (id) => ({ method: 'delete', url: `${BASE}/mail/accounts/${id}` }),
    }),
    deleteMailTemplate: build.mutation<{ id: number }, number>({
      invalidatesTags: [RESEARCH_MAIL_TAG],
      query: (id) => ({
        method: 'delete',
        url: `${BASE}/mail/templates/${id}`,
      }),
    }),
    needsShow: build.query<{ rows: NeedsShowRow[] }, void>({
      providesTags: [RESEARCH_MAIL_TAG],
      query: () => ({
        headers,
        method: 'get',
        url: `${BASE}/emails/needs-show`,
      }),
    }),
    showEmails: build.query<{ rows: ShowEmail[] }, number>({
      providesTags: [RESEARCH_MAIL_TAG],
      query: (showId) => ({
        headers,
        method: 'get',
        url: `${BASE}/emails?showId=${showId}`,
      }),
    }),
    mailTemplates: build.query<
      { variables: string[]; templates: MailTemplate[] },
      void
    >({
      providesTags: [RESEARCH_MAIL_TAG],
      query: () => ({ headers, method: 'get', url: `${BASE}/mail/templates` }),
    }),
    reconcileEmail: build.mutation<EmailState, number>({
      invalidatesTags: [RESEARCH_MAIL_TAG],
      ...refreshStatusLine,
      query: (id) => ({
        method: 'post',
        url: `${BASE}/emails/${id}/reconcile`,
      }),
    }),
    inboxThread: build.query<InboxThread, string>({
      providesTags: [RESEARCH_INBOX_TAG],
      query: (threadId) => ({
        headers,
        method: 'get',
        url: `${BASE}/inbox/threads/${encodeURIComponent(threadId)}`,
      }),
    }),
    inboxThreads: build.query<InboxThreadsPage, InboxThreadsRequest>({
      providesTags: [RESEARCH_INBOX_TAG],
      query: (request) => ({
        headers,
        method: 'get',
        url: `${BASE}/inbox/threads${threadsQuery(request)}`,
      }),
    }),
    sendMail: build.mutation<EmailState, SendRequest>({
      invalidatesTags: [
        RESEARCH_MAIL_TAG,
        RESEARCH_INBOX_TAG,
        RESEARCH_BADGE_TAG,
      ],
      ...refreshStatusLine,
      query: (data) => ({ data, method: 'post', url: `${BASE}/emails/send` }),
    }),
    dismissEmail: build.mutation<{ id: number }, number>({
      invalidatesTags: [
        RESEARCH_MAIL_TAG,
        RESEARCH_INBOX_TAG,
        RESEARCH_BADGE_TAG,
      ],
      ...refreshStatusLine,
      query: (id) => ({ method: 'post', url: `${BASE}/emails/${id}/dismiss` }),
    }),
    resolveEmail: build.mutation<
      { id: number; status: string },
      { id: number; sent: boolean }
    >({
      invalidatesTags: [RESEARCH_MAIL_TAG],
      ...refreshStatusLine,
      query: ({ id, sent }) => ({
        method: 'post',
        data: { sent },
        url: `${BASE}/emails/${id}/resolve`,
      }),
    }),
    saveMailTemplate: build.mutation<
      MailTemplate,
      { id?: number; data: MailTemplateFields }
    >({
      invalidatesTags: [RESEARCH_MAIL_TAG],
      query: ({ id, data }) => ({
        data,
        method: id ? 'patch' : 'post',
        url: id ? `${BASE}/mail/templates/${id}` : `${BASE}/mail/templates`,
      }),
    }),
    setFollowUp: build.mutation<
      { emailId: number; followUp: FollowUp | null },
      { id: number; done?: true; followUpDays?: FollowUpDays }
    >({
      invalidatesTags: [RESEARCH_INBOX_TAG, RESEARCH_BADGE_TAG],
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: `${BASE}/emails/${id}/follow-up`,
      }),
    }),
    assignEmail: build.mutation<
      { id: number; showId: number },
      { id: number; showId: number }
    >({
      invalidatesTags: [
        RESEARCH_MAIL_TAG,
        RESEARCH_INBOX_TAG,
        RESEARCH_BADGE_TAG,
      ],
      ...refreshStatusLine,
      query: ({ id, showId }) => ({
        method: 'post',
        data: { showId },
        url: `${BASE}/emails/${id}/assign`,
      }),
    }),
    markThread: build.mutation<
      { unread: boolean; threadId: string },
      { unread: boolean; threadId: string }
    >({
      invalidatesTags: [RESEARCH_BADGE_TAG],
      query: ({ unread, threadId }) => ({
        method: 'post',
        url: `${BASE}/inbox/threads/${encodeURIComponent(threadId)}/${unread ? 'unread' : 'read'}`,
      }),
      // The open thread and every cached list page show it at once; no
      // refetch of the thread (its 50 message reads) or the list.
      onQueryStarted: async (
        { unread, threadId },
        { dispatch, getState, queryFulfilled }
      ) => {
        const patches = [
          dispatch(
            researchMailApi.util.updateQueryData(
              'inboxThread',
              threadId,
              (thread) => {
                const last = thread.messages[thread.messages.length - 1];
                for (const message of thread.messages) {
                  message.unread = unread && message === last;
                }
              }
            )
          ),
          ...researchMailApi.util
            .selectInvalidatedBy(getState(), [RESEARCH_INBOX_TAG])
            .filter((entry) => entry.endpointName === 'inboxThreads')
            .map((entry) =>
              dispatch(
                researchMailApi.util.updateQueryData(
                  'inboxThreads',
                  entry.originalArgs,
                  (page) => {
                    for (const row of page.rows) {
                      if (row.threadId === threadId) {
                        row.unread = unread;
                      }
                    }
                  }
                )
              )
            ),
        ];
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
    }),
  }),
});

export const {
  useMailBadgeQuery,
  useNeedsShowQuery,
  useEmailStateQuery,
  useShowEmailsQuery,
  useInboxThreadQuery,
  useSendMailMutation,
  useInboxThreadsQuery,
  useMailAccountsQuery,
  useMarkThreadMutation,
  useMailTemplatesQuery,
  useSetFollowUpMutation,
  useAssignEmailMutation,
  useConnectMailMutation,
  usePreviewMailMutation,
  useDismissEmailMutation,
  useResolveEmailMutation,
  useDisconnectMailMutation,
  useReconcileEmailMutation,
  useSaveMailTemplateMutation,
  useDeleteMailTemplateMutation,
} = researchMailApi;
